/**
 * Approvals — maker-checker inbox for wallet adjustment proposals.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { PageHead } from "../components/PageHead";
import { EmptyState, ErrorCard, LoadingCard } from "../components/Async";
import { useAdminQuery, shortDateTime, tzs, errorMessage } from "../lib/admin";
import { adminApi } from "../lib/api";
import type { Proposal } from "../lib/types";
import { Check, X, Banknote, AlertTriangle, ArrowDownRight, ArrowUpRight } from "lucide-react";

interface ApprovalsResponse {
  data: Proposal[];
  total: number;
}

export function Approvals() {
  const queryClient = useQueryClient();
  const [rejectFor, setRejectFor] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [actionError, setActionError] = useState<string | null>(null);

  const list = useAdminQuery<ApprovalsResponse>(["approvals"], "/admin/v1/approvals");

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin"] });
    setActionError(null);
  };

  const approve = useMutation({
    mutationFn: (ref: string) => adminApi.post(`/admin/v1/approvals/${ref}/approve`),
    onSuccess: invalidate,
    onError: (error) => setActionError(errorMessage(error)),
  });

  const reject = useMutation({
    mutationFn: (input: { ref: string; reason: string }) =>
      adminApi.post(`/admin/v1/approvals/${input.ref}/reject`, undefined, {
        query: { reason: input.reason },
      }),
    onSuccess: () => { invalidate(); setRejectFor(null); setReason(""); },
    onError: (error) => setActionError(errorMessage(error)),
  });

  const items = list.data?.data ?? [];

  return (
    <>
      <PageHead
        title="Approvals"
        description="Maker-checker inbox for wallet adjustments and privileged actions."
        actions={<span className="ev-badge ev-badge-amber">{items.length} pending</span>}
      />

      {actionError && (
        <div className="admin-note" style={{ marginBottom: 12 }}>
          <AlertTriangle size={16} />
          <span>{actionError}</span>
        </div>
      )}

      {list.isLoading && <LoadingCard label="Loading approvals…" />}
      {list.error && <ErrorCard error={list.error} onRetry={() => void list.refetch()} />}
      {list.data && items.length === 0 && <EmptyState>No pending approvals.</EmptyState>}

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {items.map((a) => (
          <div key={a.ref} className="admin-card" style={{ padding: 20 }}>
            <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
              <div style={{ width: 40, height: 40, borderRadius: "var(--ev-radius-md)", background: "var(--ev-amber-100)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Banknote size={18} style={{ color: "var(--ev-amber-600)" }} />
              </div>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                  <span className="ev-body-semibold">
                    {a.direction === "credit" ? "Credit" : "Debit"} {tzs(a.amount_minor)}
                  </span>
                  <span className="ev-badge ev-badge-blue">{a.type.replaceAll("_", " ")}</span>
                  <span className="ev-body-mono ev-body-micro muted">{a.ref}</span>
                </div>
                <div className="ev-body-muted">
                  Wallet <span className="ev-body-mono">{a.wallet_id}</span>
                  {a.reason_code ? ` · ${a.reason_code}` : ""}
                  {a.note ? ` · ${a.note}` : ""}
                </div>
                <div className="ev-body-micro muted" style={{ marginTop: 6 }}>
                  Requested by {a.requested_by} · {shortDateTime(a.created_at)}
                </div>

                {rejectFor === a.ref && (
                  <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                    <input
                      className="ev-input"
                      placeholder="Rejection reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      style={{ flex: 1, minWidth: 200 }}
                    />
                    <button
                      className="ev-btn ev-btn-sm ev-btn-outline"
                      disabled={reject.isPending}
                      onClick={() => reject.mutate({ ref: a.ref, reason })}
                    >
                      Confirm reject
                    </button>
                    <button className="ev-btn ev-btn-sm ev-btn-ghost" onClick={() => { setRejectFor(null); setReason(""); }}>
                      Cancel
                    </button>
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                <span className="ev-body-micro muted" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  {a.direction === "credit" ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                  {a.direction}
                </span>
                <button
                  className="ev-btn ev-btn-sm ev-btn-outline"
                  onClick={() => { setRejectFor(a.ref); setReason(""); }}
                >
                  <X size={16} />
                </button>
                <button
                  className="ev-btn ev-btn-sm ev-btn-primary"
                  disabled={approve.isPending}
                  onClick={() => approve.mutate(a.ref)}
                >
                  <Check size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
